import usePageTitle from "@/hooks/usePageTitle";
import { useNavigate, Path } from "@/router";
import usePeopleQuery from "./_hooks/usePeopleQuery";
import PeopleTable from "./_components/PeopleTable";
import useTableRowMenu from "@/hooks/useTableRowMenu";
import { useState, useEffect, useRef } from "react";
import CreateModal from "./_components/CreateModal";
import CreateTestPersonModal from "./_components/CreateTestPersonModal";
import { useHasCapabilityBool } from "@/hooks/useCapabilities";
import { useFlags } from "@/hooks/useFeatureFlags";
import { DOMAIN, PERMISSION } from "../../providers/AuthorizationProvider/types";
import { Query } from "./_hooks/useSearchParams";
import { useSearchParams } from "react-router-dom";
import { type TableAction } from "@/components/Table";
import { IndexTabBarActions } from "@/components/Tabs/IndexTabBarActions";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import { useBulkSelection } from "@/hooks/useBulkSelection";
import TabLayout from "./_TabLayout";
import { isDev } from "@/lib/urlUtils";

export default function PeoplePage() {
  usePageTitle("/people", "People");
  const navigate = useNavigate();
  const [showAdd, setShowAdd] = useState(false);
  const [showCreateTestPerson, setShowCreateTestPerson] = useState(false);
  const mayCreatePerson = useHasCapabilityBool({ domain: DOMAIN.PEOPLE, permission: PERMISSION.managePeople });
  const flags = useFlags();
  const canCreateTestPerson = !!flags["backoffice-create-test-person"] && mayCreatePerson && isDev();

  const [searchParams] = useSearchParams();
  const [advancedSearchQuery, setAdvancedSearchQuery] = useState<Partial<Query>>({});

  const bulkSelection = useBulkSelection();
  const { isSelectedOnly, orderedSelectedIds } = bulkSelection;

  useEffect(() => {
    const query: Partial<Query> = {};
    const fields = ["email", "phone", "contactPostalCode"] as const;

    fields.forEach((field) => {
      const value = searchParams.get(field);
      if (value) {
        query[field] = value;
      }
    });

    setAdvancedSearchQuery(query);
  }, [searchParams]);

  const selectedPgaIds = isSelectedOnly ? orderedSelectedIds : undefined;
  const { resetPagination, ...tableProps } = usePeopleQuery(advancedSearchQuery, selectedPgaIds);

  const prevIsSelectedOnlyRef = useRef(isSelectedOnly);
  useEffect(() => {
    if (prevIsSelectedOnlyRef.current !== isSelectedOnly) {
      prevIsSelectedOnlyRef.current = isSelectedOnly;
      resetPagination();
    }
  }, [isSelectedOnly, resetPagination]);
  const { Menu, setMenu } = useTableRowMenu();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);

  const actions: TableAction[] = mayCreatePerson
    ? [
        {
          label: "Create Person",
          icon: "add" as const,
          onClick: () => {
            setShowAdd(true);
          },
          testId: "create-person-button"
        },
        ...(canCreateTestPerson
          ? [
              {
                label: "Create Test Person",
                icon: "add" as const,
                onClick: () => {
                  setShowCreateTestPerson(true);
                },
                testId: "create-test-person-button",
                variant: "outlined" as const
              }
            ]
          : [])
      ]
    : [];

  return (
    <TabLayout>
      <IndexTabBarActions actions={actions} />
      <Menu />
      <div className="w-full">
        <div className="pt-6" />
        <PeopleTable
          {...tableProps}
          setMenu={setMenu}
          advancedSearchQuery={advancedSearchQuery}
          bulkSelection={bulkSelection}
          onViewPerson={(pgaId: string) => {
            setSelectedPgaId(pgaId);
            setIsPersonSideSheetOpen(true);
          }}
        />
      </div>
      {showAdd && (
        <CreateModal
          onClosed={() => {
            setShowAdd(false);
          }}
        />
      )}
      {showCreateTestPerson && (
        <CreateTestPersonModal
          onClosed={() => {
            setShowCreateTestPerson(false);
          }}
        />
      )}
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
        customActionButtons={
          selectedPgaId
            ? [
                {
                  text: "View Person",
                  variant: "outlined" as const,
                  onClick: () => {
                    navigate(
                      `/people/${selectedPgaId}/person/overview?return_to=/people&${tableProps.searchParamsUrl}` as Path
                    );
                  }
                }
              ]
            : undefined
        }
      />
    </TabLayout>
  );
}
